/*
  Map click routing.
  New layer types:
  1. Add a ClickHandlerId (if needed) and preset config below.
  2. registerLayerClickConfig(registry, layerId, config) when the layer is added.
  3. unregisterLayerClickConfig(registry, layerId) when removed.
  4. Add a switch case in Dashboard click handler if the handler needs new UI logic.
 */

/* How a map layer responds to feature clicks. Register when the layer is added. */
export type ClickHandlerId =
  | 'survey-coverage'
  | 'survey-image-point'
  | 'attributes'
  | 'none';

export type HighlightMode = 'dashboard' | 'image-viewer' | 'none';

export interface LayerClickConfig {
  readonly clickHandler: ClickHandlerId;
  readonly highlightMode: HighlightMode;
  /* Omit start/end markers on polyline clicks (e.g. survey coverage routes). */
  readonly skipStartEndMarkers?: boolean;
}

export type LayerClickConfigRegistry = Record<string, LayerClickConfig>;

/* Reusable configs — pass to registerLayerClickConfig when adding a layer. */
export const LEGEND_LAYER_CLICK_CONFIG: LayerClickConfig = {
  clickHandler: 'attributes',
  highlightMode: 'dashboard',
};

export const SURVEY_COVERAGE_CLICK_CONFIG: LayerClickConfig = {
  clickHandler: 'survey-coverage',
  highlightMode: 'dashboard',
  skipStartEndMarkers: true,
};

export const SURVEY_IMAGE_POINT_CLICK_CONFIG: LayerClickConfig = {
  clickHandler: 'survey-image-point',
  highlightMode: 'image-viewer',
};

export function registerLayerClickConfig(
  registry: LayerClickConfigRegistry,
  layerId: string,
  config: LayerClickConfig,
): void {
  registry[layerId] = config;
}

export function unregisterLayerClickConfig(
  registry: LayerClickConfigRegistry,
  layerId: string,
): void {
  delete registry[layerId];
}


export function registerSurveyCoverageClickConfig(
  registry: LayerClickConfigRegistry,
  layerId: string,
  coverageKeys: Set<string>,
): void {
  coverageKeys.add(layerId);
  registerLayerClickConfig(registry, layerId, SURVEY_COVERAGE_CLICK_CONFIG);
}

export function unregisterSurveyCoverageClickConfig(
  registry: LayerClickConfigRegistry,
  layerId: string,
  coverageKeys: Set<string>,
): void {
  coverageKeys.delete(layerId);
  unregisterLayerClickConfig(registry, layerId);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/* Layer registration takes priority; attribute checks are legacy fallback for older layers. */
export function resolveClickHandlerId(
  layerId: string,
  attributes: unknown,
  registry: LayerClickConfigRegistry,
): ClickHandlerId {
  const registered = registry[layerId]?.clickHandler;
  if (registered) {
    return registered;
  }

  if (!isRecord(attributes)) {
    return 'attributes';
  }

  // New layers should call registerLayerClickConfig instead.
  const surveyType = attributes.survey_type ?? attributes.surveyType;
  const surveySlug = attributes.survey_slug ?? attributes.surveySlug;
  const filePath = attributes.file_path ?? attributes.filePath;
  const imageType = attributes.image_type ?? attributes.imageType;

  if ((surveyType || surveySlug) && !filePath) {
    return 'survey-coverage';
  }
  if (filePath && imageType) {
    return 'survey-image-point';
  }

  return 'attributes';
}

export function resolveHighlightMode(
  layerId: string,
  handlerId: ClickHandlerId,
  registry: LayerClickConfigRegistry,
): HighlightMode {
  const configured = registry[layerId]?.highlightMode;
  if (configured) {
    return configured;
  }
  if (handlerId === 'survey-image-point') {
    return 'image-viewer';
  }
  if (handlerId === 'none') {
    return 'none';
  }
  return 'dashboard';
}

export function shouldSkipStartEndMarkers(
  layerId: string,
  registry: LayerClickConfigRegistry,
): boolean {
  return registry[layerId]?.skipStartEndMarkers === true;
}

/* Only handlers that open the Attributes modal should push attributesModalData. */
export function updatesAttributesModal(handlerId: ClickHandlerId): boolean {
  return handlerId === 'attributes';
}
